import { GridRowModel } from "@mui/x-data-grid-premium";
import { IndexGenerator } from "fractional-indexing-jittered";

/**
 * Assigns new ranks to all unranked rows above the specified target index.
 * Returns a new array with updated rows, preserving immutability.
 *
 * @param rows - The array of GridRowModel objects to process.
 * @param targetIndex - The index up to which unranked rows will be assigned ranks (exclusive).
 * @param startRank - The initial rank to continue from; if falsy, ranking starts from the beginning.
 * @param generator - The IndexGenerator instance to use for rank generation.
 * @param groupId - The group identifier for rank generation.
 * @returns A new array of GridRowModel objects with assigned ranks for previously unranked rows above targetIndex.
 */
export function assignRanksToUnrankedRowsAboveIndex(
    rows: Array<GridRowModel>,
    targetIndex: number,
    startRank: string | null,
    generator: IndexGenerator,
    groupId: string
): Array<GridRowModel> {
    
    const unrankedRows = rows.slice(0, targetIndex).filter(row => !row.rank);
    const ranks: string[] = startRank ? 
        generator.nKeysAfter(startRank, unrankedRows.length) : 
        generator.nKeysStart(unrankedRows.length, groupId);

    let rankIdx = 0;
    const updatedRows = rows.map((row, i) => {
        if (i < targetIndex && !row.rank) {
            const updatedRow = { ...row, rank: ranks[rankIdx] };
            rankIdx++;
            return updatedRow;
        }
        return row;
    });

    return updatedRows;
}

/**
 * Generates a unique rank for a moved item deriving its previous and next ranks,
 * resolving any collisions using resolveRankCollision.
 *
 * @param rows - The array of rows (used for recursive collision resolution).
 * @param targetIndex - The index of the next item after the insertion point.
 * @param generator - The IndexGenerator instance to use for rank generation.
 * @param groupId - The group identifier for rank generation.
 * @returns An object containing the new rank for the moved item and the updated array of GridRowModel objects with resolved ranks.
 */
export function getRankForMovedItem(
    rows: Array<GridRowModel>,
    targetIndex: number,
    generator: IndexGenerator, 
    groupId: string
): { rank: string; updatedRows: Array<GridRowModel> } {
    let collisionDetected = false;
    let newRank: string;
    let updatedRows: Array<GridRowModel>;
    
    const prevRank = targetIndex > 0 ? rows[targetIndex - 1]?.rank : null;
    let nextRank = rows[targetIndex]?.rank;
    
    // If prev and next have the same rank, recursively re-rank the next row(s)
    if (prevRank && nextRank && prevRank === nextRank) {
        collisionDetected = true;
        updatedRows = resolveRankCollision(rows, targetIndex, generator, groupId);
        // update nextRank to the new value
        nextRank = updatedRows[targetIndex]?.rank;
        generator.updateList(
            updatedRows
                .filter(row => !!row.rank)
                .map(row => row.rank)
        );
    }

    // Generating rank for moved item between prevRank and nextRank
    if (prevRank && nextRank) {
        newRank = generator.keyAfter(prevRank);
    } else if (prevRank) {
        newRank = generator.keyAfter(prevRank);
    } else if (nextRank) {
        newRank = generator.keyBefore(nextRank);
    } else {
        newRank = generator.keyStart(groupId);
    }
    
    if (collisionDetected) {
        return { rank: newRank, updatedRows };
    }

    return { rank: newRank, updatedRows: rows };
}

/**
 * Resolves rank collisions by generating and assigning new unique ranks
 * to items at and after `startIndex` that have the same rank as the item
 * immediately before `startIndex`.
 *
 * @param rows - The array of GridRowModel objects to process.
 * @param startIndex - The index at which to start resolving rank collisions (the first
 * item whose rank is equal to the previous item's rank).
 * @param generator - The IndexGenerator instance to use for rank generation.
 * @param groupId - The group identifier for rank generation.
 * @returns The updated array of GridRowModel objects with resolved ranks.
 */
export function resolveRankCollision(
    rows: Array<GridRowModel>,
    startIndex: number,
    generator: IndexGenerator,
    groupId: string
): Array<GridRowModel> {
    const prevRank = startIndex > 0 ? rows[startIndex - 1]?.rank : null;
    // Find the chain of items with the same rank
    let endIndex = startIndex;
    const collisionRank = prevRank;
    while (
        endIndex < rows.length &&
        rows[endIndex]?.rank === collisionRank
    ) {
        endIndex++;
    }

    const nextDistinctRank = rows[endIndex]?.rank;
    const numKeys = endIndex - startIndex;
    const newRanks = nextDistinctRank ? 
        generator.nKeysBefore(nextDistinctRank, numKeys) :
        generator.nKeysEnd(numKeys, groupId);

    // Assign new ranks to the colliding items (except prevRank item)
    const updatedRows = rows.map((row, idx) => {
        if (idx >= startIndex && idx < startIndex + numKeys) {
            return { ...row, rank: newRanks[idx - startIndex] };
        }
        return row;
    });

    return updatedRows;
}

/**
 * Reorders rows by moving the row at initialIndex to targetIndex,
 * assigning ranks as necessary to maintain order.
 *
 * @param initialIndex - The original index of the row to be moved.
 * @param targetIndex - The index where the row should be moved to.
 * @param rows - The array of GridRowModel objects representing the rows.
 * @param groupId - The group identifier for rank generation.
 * @returns A new array of the same row type with updated ranks reflecting the new order.
 */
export function reorderRowsWithRanks<T extends GridRowModel = GridRowModel>(
    initialIndex: number,
    targetIndex: number,
    rows: Array<T>,
    groupId: string
): Array<T> {
    const rowsClone = [...rows];
    const movedRow = rowsClone.splice(initialIndex, 1)[0];
    const itemsAboveAtMove = rowsClone.slice(0, targetIndex);
    const rankedAboveAtMove = itemsAboveAtMove.filter(row => !!row.rank);

    let lastRank: string | null = null;
    let lastRankAbove: string | null = null;
    let newRankOfMovedItem: string | null;
    let workingRows: Array<GridRowModel> = [...rowsClone];
    let rankList = rowsClone.filter(row => !!row.rank).map(row => row.rank);

    const generator = new IndexGenerator(rankList,{ useJitter: true, groupIdLength: groupId.length });
    
    if (targetIndex === 0) {
        newRankOfMovedItem = generator.keyStart(groupId);
        workingRows.splice(targetIndex, 0, { ...movedRow, rank: newRankOfMovedItem });
        return workingRows as Array<T>;
    }

    if (itemsAboveAtMove.length > 0) {
        if (rankedAboveAtMove.length > 0) {
            lastRankAbove = rankedAboveAtMove[rankedAboveAtMove.length - 1].rank;
        }
        if (rankedAboveAtMove.length < itemsAboveAtMove.length) {
            const rowsWithAssignedRanks =
                assignRanksToUnrankedRowsAboveIndex(rowsClone, targetIndex, lastRankAbove, generator, groupId);
            workingRows = [...rowsWithAssignedRanks];
            lastRank = rowsWithAssignedRanks[targetIndex - 1]?.rank || null;
        }
    }

    rankList = workingRows.filter(row => !!row.rank).map(row => row.rank);
    generator.updateList(rankList);

    if (itemsAboveAtMove.length > 0 && rankedAboveAtMove.length === 0) {
        newRankOfMovedItem = lastRank ? generator.keyAfter(lastRank) : generator.keyStart(groupId);
    } else {
        const result = getRankForMovedItem(workingRows, targetIndex, generator, groupId);
        newRankOfMovedItem = result.rank;
        workingRows = result.updatedRows;
    }

    const updatedMovedRow = { ...movedRow, rank: newRankOfMovedItem };
    workingRows.splice(targetIndex, 0, updatedMovedRow);

    return workingRows as Array<T>;
}
